/*
 *  Copyright (c) 2012-2021 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved. NOTICE: All information contained herein
 *  is, and remains the property of GRAB. The intellectual and technical concepts contained herein are confidential, proprietary
 *  and controlled by GRAB and may be covered by patents, patents in process, and are protected by trade secret or copyright law.
 *
 *  You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate, adapt or change this material
 *  in any way unless prior written permission is obtained from GRAB. Access to the source code contained herein is hereby
 *  forbidden to anyone except current GRAB employees or contractors with binding Confidentiality and Non-disclosure agreements
 *  explicitly covering such access.
 *
 *  The copyright notice above does not evidence any actual or intended publication or disclosure of this source code,
 *  which includes information that is confidential and/or proprietary, and is a trade secret, of GRAB.
 *
 *  ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE
 *  CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE LAWS AND
 *  INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY
 *  OR IMPLY ANY RIGHTS TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING
 *  THAT IT MAY DESCRIBE, IN WHOLE OR IN PART.
 *
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import './camera-group.less'
import { Button, Input, Select, Space, Form, Modal } from 'antd'
import { IDeviceListQuery, getDeviceList, IDeviceList } from 'api/devices'
import { debounce, curry } from 'lodash'
import { IGroupCamera } from '../../api/setting'
import classnames from 'classnames';
import { CameraDirection } from '../../constants/setting'
enum CameraStatus {
  unSelected = 'unselected',
  processing = 'processing',
  selected = 'selected'
}
export interface values {
  left?: IGroupCamera
  right?: IGroupCamera
  back?: IGroupCamera
  front: IGroupCamera
}
export enum settingMode {
  query,
  operate
}
const icons = [{
  key: 'front',
  text: "F",
}, {
  key: 'right',
  text: "R",
}, {
  key: 'back',
  text: "B",
}, {
  key: 'left',
  text: "L",
}]

export interface ISlaveCamera {
  direction: string,
  deviceID: string
}
interface IOnchange {
  (args: ISlaveCamera):void,
}

const CameraGroupSetting = ({ values, onChange, mode = settingMode.operate }: { values: values, onChange: IOnchange, mode: settingMode }) => {
  const [options, setOptions] = useState([]);
  const [hoveredKey, setHoveredKey] = useState();
  const initState = {
    left: { value: values?.left?.deviceID },
    right: { value: values?.right?.deviceID },
    back: { value: values?.back?.deviceID },
    front: { value: values?.front?.deviceID }
  }
  const { cityID } = values.front;
  const [cameraStates, setCameraState] = useState(initState)
  const [form] = Form.useForm()

  const onInputSearch = async (keyword) => {
    if (!keyword || !keyword.length) {
      clearOptions();
      return;
    }
    const list = await getDeviceList({ page: 1, pageSize: 1000, keyword, cityID })
    const selectedIds=Object.values(cameraStates).map(x=>x.value).filter(x=>!!x);
    const newOptions = list.results.map(({ deviceID }) => {
      let disabled=Number(deviceID.slice(-1)) < 3
      if(!disabled && selectedIds.indexOf(deviceID)>-1){
        disabled=true;
      }
     return  { label: deviceID, value: deviceID, disabled }
    })
    setOptions(newOptions);
  }


  const onFocusHandle = (key) => () => {
    setHoveredKey(key)
  }
  const clearOptions = () => {
    setOptions([]);
  }
  const onBlur = () => {
    clearOptions()
    setHoveredKey(null)
  }

  const handleInputSearch = debounce(onInputSearch, 1000);

  const onValuesChange = (changedValue) => {
    const key = Object.keys(changedValue)[0]
    const value = changedValue[key].value;
    setCameraState(cameraStates => {
      return { ...cameraStates, ...changedValue }
    })
    onChange({ direction: key, deviceID: value })
    onBlur();
  }
  const detachHandle = (key: string) =>()=> {
    const target = cameraStates[key]
    const title = (
      <div style={{ fontFamily: 'SanomatGrabApp' }}>
        <header style={{ fontSize: '16px', marginBottom: '15px' }}>
          You are detaching a camera from this 360-lite group
        </header>
        <p style={{ fontSize: '16px', color: 'rgba(0,0,0,0.65)' }}>
          This may result in the loss of parts of a 360 image that has not been uploaded as part of this 360-lite camera. Are you sure you want to continue?
        </p>
      </div>
    )
    Modal.confirm({
      title: title,
      centered: true,
      width: 490,
      onOk: () => {
        setCameraState({ ...cameraStates, [key]: { ...target, value: undefined, keyword: '' } })
        form.setFieldsValue({ [key]: undefined })
        onChange({ direction: key, deviceID: ''})
      },
      cancelText: 'Return to settings',
      okText: 'Continue',
      okButtonProps: { type: 'primary', danger: true }
    })
  }


  return (
    <div className={'camera-group'}>
      <div className={'header'}>
        360-lite Camera Set Up:
      </div>
      <div className={'sider'}>
        {
          icons.map((icon) => (
            <i
              key={icon.key}
              className={classnames(icon.key, {
                [CameraStatus.selected]: !!cameraStates[icon.key].value,
                [CameraStatus.processing]: hoveredKey === icon.key
                })}
              data-content={icon.text}
            />
            )
          )
        }

      </div>
      <Form
        layout={'vertical'}
        form={form}
        className={'content-form'}
        initialValues={initState}
        onValuesChange={onValuesChange}
      >
        <Form.Item label={<span className={'label'}>Front (master)</span>}>
          <Form.Item noStyle={true} name={['front', 'value']}>
            <Input
              style={{ width: '100%' }}
              placeholder={'input camera ID'}
              disabled={true}
            />
          </Form.Item>
          <footer>The camera that the 360-lite setting is initiated from will always be the front and master camera</footer>
        </Form.Item>
        <Form.Item label={<span className={'label'}>Right (Slave 1)</span>}>
          <Form.Item noStyle={true} name={['right', 'value']}>
            <Select
              placeholder={'input camera ID'}
              showSearch={true}
              onSearch={handleInputSearch}
              style={{ display: 'inline-block', width: 'calc(100% - 80px)', marginRight: '10px' }}
              options={options}
              onFocus={onFocusHandle('right')}
              onBlur={onBlur}
              disabled={!!cameraStates.right.value || mode === settingMode.query}
            />
          </Form.Item>
          <Button
            type={'primary'}
            onClick={detachHandle( 'right')}
            style={{ width: 70 }}
            disabled={!cameraStates.right.value || mode === settingMode.query}
          >
            Detach
          </Button>
        </Form.Item>
        <Form.Item label={<span className={'label'}>Back (Slave 2)</span>}>
          <Form.Item noStyle={true} name={['back', 'value']}>
            <Select
              placeholder={'input camera ID'}
              showSearch={true}
              onSearch={handleInputSearch}
              style={{ display: 'inline-block', width: 'calc(100% - 80px)', marginRight: '10px' }}
              options={options}
              onFocus={onFocusHandle('back')}
              onBlur={onBlur}
              disabled={!!cameraStates.back.value || mode === settingMode.query}
            />
          </Form.Item>
          <Button
            type={'primary'}
            onClick={detachHandle( 'back')}
            style={{ width: 70 }}
            disabled={!cameraStates.back.value || mode === settingMode.query}
          >
            Detach
          </Button>
        </Form.Item>
        <Form.Item label={<span className={'label'}>Left (Slave 3)</span>}>
          <Form.Item noStyle={true} name={['left', 'value']}>
            <Select
              placeholder={'input camera ID'}
              showSearch={true}
              onSearch={handleInputSearch}
              style={{ display: 'inline-block', width: 'calc(100% - 80px)', marginRight: '10px' }}
              options={options}
              onFocus={onFocusHandle('left')}
              onBlur={onBlur}
              disabled={!!cameraStates.left.value || mode === settingMode.query}
            />
          </Form.Item>
          <Button
            type={'primary'}
            onClick={detachHandle('left')}
            style={{ width: 70 }}
            disabled={!cameraStates.left.value || mode === settingMode.query}
          >
            Detach
          </Button>
        </Form.Item>
      </Form>
    </div>
  )
}
export default CameraGroupSetting;
